import { useState } from 'react';
import { Search } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { EYEBROW, FOCUS_RING, FRAMES, LINES, SHAPES, TILE } from '../constants';
import { PanelHeading } from '../controls';
import { ART_OBJECTS, ART_ORNAMENTS } from '../elementArt';
import { assetUrl } from '../../design/DesignLayers';

/** A grid of built-in pictures (simcha objects, ornaments). */
const ArtGrid = ({ items, onAdd, testPrefix }) => (
  <div className="grid grid-cols-3 gap-2">
    {items.map((a) => (
      <button
        key={a.id}
        type="button"
        onClick={() => onAdd(a.items)}
        aria-label={`Add ${a.name.toLowerCase()}`}
        title={a.name}
        className={cn(TILE, 'aspect-square p-2 flex flex-col items-center justify-center gap-1')}
        data-testid={`${testPrefix}-${a.id}`}
      >
        <img src={assetUrl(a.src)} alt="" loading="lazy" className="h-[58px] w-full object-contain" />
        <span className="text-[10.5px] font-bold text-[#4A4640] leading-tight text-center line-clamp-1">{a.name}</span>
      </button>
    ))}
  </div>
);

const LinePreview = ({ id }) => {
  if (id === 'ornament') {
    return (
      <span className="flex-1 flex items-center gap-2.5">
        <span className="flex-1 h-px bg-gold" />
        <span className="h-2 w-2 rotate-45 bg-gold" />
        <span className="flex-1 h-px bg-gold" />
      </span>
    );
  }
  const bg = {
    solid: '#B09536',
    dashed: 'repeating-linear-gradient(90deg, #1A1A1A 0 6px, transparent 6px 10px)',
    dotted: 'radial-gradient(circle, #1A1A1A 1.5px, transparent 2px) 0 50% / 6px 3px repeat-x',
  }[id];
  return <span className="flex-1 h-[3px]" style={{ background: bg }} />;
};

/** Elements tool: simcha pictures, ornaments, shapes, lines & dividers and frames. */
export const ElementsPanel = ({ onAdd }) => {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const match = (item) => !q || `${item.name} ${item.keywords}`.toLowerCase().includes(q);
  const shapes = SHAPES.filter(match);
  const lines = LINES.filter(match);
  const frames = FRAMES.filter(match);
  const objects = ART_OBJECTS.filter(match);
  const ornaments = ART_ORNAMENTS.filter(match);

  return (
    <div className="flex flex-col gap-3.5">
      <PanelHeading>Elements</PanelHeading>
      <label className="h-[42px] rounded-full gradient-border flex items-center gap-2 px-3.5 focus-within:ring-[3px] focus-within:ring-pink/10">
        <Search className="h-4 w-4 text-[#6B665C]" aria-hidden="true" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search wine, tefillin, frames, flowers…"
          aria-label="Search elements"
          className="flex-1 min-w-0 bg-transparent outline-none text-sm"
        />
      </label>

      {objects.length > 0 && (
        <>
          <p className={EYEBROW}>Simcha pictures</p>
          <ArtGrid items={objects} onAdd={onAdd} testPrefix="add-art" />
        </>
      )}

      {ornaments.length > 0 && (
        <>
          <p className={EYEBROW}>Ornaments for the design</p>
          <ArtGrid items={ornaments} onAdd={onAdd} testPrefix="add-ornament" />
        </>
      )}

      {shapes.length > 0 && (
        <>
          <p className={EYEBROW}>Shapes</p>
          <div className="grid grid-cols-4 gap-2">
            {shapes.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => onAdd(s.items)}
                aria-label={`Add ${s.name.toLowerCase()}`}
                title={s.name}
                className={cn(TILE, 'aspect-square flex items-center justify-center')}
                data-testid={`add-shape-${s.id}`}
              >
                <span className="h-[34px] w-[34px] box-border" style={s.preview} />
              </button>
            ))}
          </div>
        </>
      )}

      {lines.length > 0 && (
        <>
          <p className={EYEBROW}>Dividers &amp; lines</p>
          <div className="flex flex-col gap-2">
            {lines.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => onAdd(l.items)}
                aria-label={`Add ${l.name.toLowerCase()}`}
                title={l.name}
                className={cn(TILE, 'h-9 flex items-center px-4')}
                data-testid={`add-line-${l.id}`}
              >
                <LinePreview id={l.id} />
              </button>
            ))}
          </div>
        </>
      )}

      {frames.length > 0 && (
        <>
          <p className={EYEBROW}>Frames</p>
          <div className="grid grid-cols-3 gap-2">
            {frames.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => onAdd(f.items)}
                aria-label={`Add ${f.name.toLowerCase()}`}
                title={f.name}
                className={cn('aspect-[5/7] rounded-lg bg-cream p-2.5 flex hover:bg-[#E9E5DC]', FOCUS_RING)}
                data-testid={`add-frame-${f.id}`}
              >
                <span className="flex-1 box-border" style={f.preview} />
              </button>
            ))}
          </div>
        </>
      )}

      {!objects.length && !ornaments.length && !shapes.length && !lines.length && !frames.length && (
        <p className="text-sm text-[#6B665C]">Nothing matches “{query}”.</p>
      )}
    </div>
  );
};

export default ElementsPanel;
